import { useCreateStationeryDraftMutation } from './ProductDetailsPage.graphql.generated';
import { StationeryDraftFormat } from '@graphql/generated-base';
import { useCardsRouterContext } from '@apps/card/Card.routes';

import { useExistingDraftsWarningContext } from '../ExistingDraftsWarningProvider';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { addSuppressedError } from '@shared/utils/logger';
import { cloneDeep } from 'lodash-es';
import type { DraftJson } from '@apps/card/routes/CardCustomizer/DraftJson.types';

import type { Format, MinimalRequiredTemplateDataForDraft } from '../DesignsGallery/DesignsGallery.types';
import { useRestrictedInternationalShippingWarningContext } from '../RestrictedInternationalShippingWarningProvider';
import type { StationeryProduct } from '@shared/utils/print-price/types';

type UseCustomizePremiumDigitalCardArgs = {
  eventId?: string;
  currentProduct: StationeryProduct | undefined;
  supportedFormats: Format;
};

/**
 * Both premium digital and the free Basic Digital V2 create a `digital` stationery
 * draft from the paper template and open the digital customizer. The draft itself is
 * built identically either way -- which product it is for is decided by the selected
 * product's `priceConfigId`, so the format here only steers telemetry.
 */
type DigitalDraftFormat = 'premiumDigital' | 'basicDigitalV2';

export const useCustomizePremiumDigitalCard = (args: UseCustomizePremiumDigitalCardArgs) => {
  const { eventId, supportedFormats, currentProduct } = args;
  const { existingDraftsWarningDialog, cardDraftCreated, pdpCustomizeNowClicked, restrictedInternationalShippingWarningDialog } = useCardTelemetry({});

  const { goToDigitalCustomizeDraft } = useCardsRouterContext();
  const { checkForExistingDraftOrCreateNew } = useExistingDraftsWarningContext();
  const { checkIfUserIsRestricted } = useRestrictedInternationalShippingWarningContext();

  const [createStationeryDraft, { loading }] = useCreateStationeryDraftMutation({
    // `SmartRoutingData` is the burger menu's copy of the drafts list. A string refetch only
    // refreshes queries that are active right now, and on the PDP no other consumer of
    // `GetDigitalAndPaperDraftsByEventId` is mounted — so without it the new draft lands in the
    // cache as an entity but never joins the cached `stationeryDraftsByEvent` list, and the menu's
    // card links keep pointing at the browse gallery instead of /cards/drafts until a reload.
    refetchQueries: () => ['GetDigitalAndPaperDraftsByEventId', 'GetExistingEventStationeryDraftsWithTemplate', 'SmartRoutingData'],
  });

  const handleOnCustomizePremiumDigitalCardClick = useEventCallback(
    (template: MinimalRequiredTemplateDataForDraft, subtotalInMinorUnits?: number, format: DigitalDraftFormat = 'premiumDigital') => {
      const isFree = format === 'basicDigitalV2';
      checkIfUserIsRestricted(
        StationeryDraftFormat.digital,
        { themeId: template.themeId, category: template.category },
        () => {
          // This callback is called when the RestrictedInternationalShipping warning dialog is about to be shown.
          restrictedInternationalShippingWarningDialog.enter();
        },
        () => {
          checkForExistingDraftOrCreateNew(
            template.id,
            async existingDraftsCount => {
              try {
                pdpCustomizeNowClicked({ themeId: template.themeId, supportedFormats, format });
                const cardJSON = cloneDeep(template.themeJSON) as DraftJson;

                if (!currentProduct) {
                  throw new Error('Tried creating draft with no selected product.');
                }

                const response = await createStationeryDraft({
                  variables: {
                    payload: {
                      eventId,
                      cardJSON,
                      stationeryTemplateId: template.id,
                      format: StationeryDraftFormat.digital,
                      priceConfigId: currentProduct.priceConfigId,
                    },
                  },
                });
                const maybeDraftId = response.data?.createStationeryDraft.id;

                if (maybeDraftId) {
                  cardDraftCreated({
                    draftId: maybeDraftId,
                    themeId: template.themeId,
                    stationeryTemplateCategory: template.category,
                    isFavorite: !!template.isFavorite,
                    page: 'pdpModal',
                    existingDraftsCount,
                    subtotalInMinorUnits,
                    isDigital: true,
                    isPremiumDesign: isFree ? template.premium : true,
                    format,
                    cardWidthInches: template.widthInches,
                    cardHeightInches: template.heightInches,
                  });
                  goToDigitalCustomizeDraft(maybeDraftId);
                }
              } catch (error) {
                // Swallowed for the user (no error UI on this path today) but recorded, so a failing
                // draft creation isn't a silent dead-end in New Relic. Same in useCustomizePaperCard.
                addSuppressedError(error, { context: 'useCustomizePremiumDigitalCard', themeId: template.themeId });
              }
            },
            () => {
              // This callback is called when the warning dialog is about to be shown.
              existingDraftsWarningDialog.enter();
            },
            true,
          );
        },
      );
    },
  );

  return {
    handleOnCustomizePremiumDigitalCardClick,
    loading,
  };
};
